/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { IWineFinderByZipViewProps } from '../../../modules/wine-finder-by-zip/./wine-finder-by-zip';

/**
 * Class definition responsible for redirecting to findusawine.com to find local wine retailers based on the user inputted zipcode
 */
@observer
export class WineFinderByZipView extends React.PureComponent<IWineFinderByZipViewProps, { value: string }> {
    /**
     * field representing the location being typed in by the user
     */
    @observable private _queryZipcode: string = '';

    public constructor(props: IWineFinderByZipViewProps) {
        super(props);
        this._handleQueryZipcodeChange = this._handleQueryZipcodeChange.bind(this);
        this._doQuery = this._doQuery.bind(this);
        this._handleHowToBuyNav = this._handleHowToBuyNav.bind(this);
        this._keyup = this._keyup.bind(this);
        this._smoothVerticalScrolling = this._smoothVerticalScrolling.bind(this);
        this._scrollingHelper = this._scrollingHelper.bind(this);
    }

    public componentDidMount(): void {
        window.addEventListener('hashchange', this._handleHowToBuyNav);
        // remove includes statment before going live (only check for location hash)
        if (this.props.context.request.url.requestUrl.search.includes('divId=how-to-buy') || location.hash === '#how-to-buy') {
            this._handleHowToBuyNav();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('hashchange', this._handleHowToBuyNav);
    }

    public render(): JSX.Element | null {
        return (
            <div className='wine-finder-by-zip-query'>
                <label htmlFor='zipcode'>Enter zip code to buy local</label>
                <input
                    id='zipcode'
                    type='text'
                    className='wine-finder-by-zip-input'
                    placeholder='Enter Zip'
                    value={this._queryZipcode}
                    onChange={this._handleQueryZipcodeChange}
                    onKeyUp={this._keyup}
                />
                <button className='wine-finder-by-zip-search-button' disabled={this._isInvalid()} onClick={this._doQuery}>Search</button>
            </div>
        );
    }

    /**
     * handles changes for the zipcode entered
     */
    private _handleQueryZipcodeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this._queryZipcode = event.target.value;
    }

    /**
     * checks if the enter/return key has been pressed to submit a zipcode query
     */
    private _keyup(event: React.KeyboardEvent<HTMLInputElement>): void {
        if (event.keyCode === 13 && !this._isInvalid()) {
            this._doQuery();
        }
    }

    /**
     * process to disable the Search button if the user's text input is not a 5 digit entry (findusawine.com does not support 9 digit zip codes)
     */
    private _isInvalid(): boolean {
        const regex = /^\d{5}$/;
        return (!regex.test(this._queryZipcode));
    }

    /**
     * process to send the zip code query to findusawine.com
     */
    private _doQuery(): void {
        window.open(`https://www.findusawine.com/${this.props.config.wineBrandUrl}/locate/domestic-results?location=${this._queryZipcode}&product_key=&header=&retail=true&restaurant=true`, '_blank');
    }

    /**
     * handles navigation to the #how-to-buy anchor tag (to be slightly modified before going live)
     */
    private _handleHowToBuyNav(): void {
        if (location.hash === '#how-to-buy' || !location.hash) {
            // eslint-disable-next-line react/no-find-dom-node
            const parentContainer = ReactDOM.findDOMNode(this)!.parentNode as Element;
            this._smoothVerticalScrolling(parentContainer, 300, 'top');
            // following if statement to be removed. only required while we utilize a query parameter for how-to-buy
            if (this.props.context.request.url.requestUrl.search.includes('divId=how-to-buy')) {
                history.pushState({}, '', `${this.props.context.request.url.requestUrl.origin}/?domain=www.h3wines.com`);
            } else {
                history.pushState('', document.title, window.location.pathname + window.location.search);
            }
        }
    }

    /**
     * custom process to scroll to an element (since smooth scrolling with window.ScrollTo is not supported by Safari and IE)
     */
    private _smoothVerticalScrolling(e: Element, time: number, where: string): void {
        const eTop = e.getBoundingClientRect().top;
        const eAmt = eTop / 100;
        let curTime = 0;
        while (curTime <= time) {
            window.setTimeout(this._scrollingHelper, curTime, eAmt, where);
            curTime += time / 100;
        }
    }

    /**
     * helper process for _SmoothVerticalScrolling
     */
    private _scrollingHelper(eAmt: number, where: string): void {
        if (where === 'center' || where === '') {
            window.scrollBy(0, eAmt / 2);
        }
        if (where === 'top') {
            window.scrollBy(0, eAmt / 1.044);
        }
    }

}

export default WineFinderByZipView;